<script setup lang="ts">
import { toast } from '~/utils/sonner'

const visitorSession = useVisitorSession()
const user = useUser()
const $i18n = useI18n()
const $t = $i18n.t

defineProps<{ embedded?: boolean }>()
const state = defineModel<string>({ default: 'login' })
const loginInfo = ref({
  email: '',
  password: '',
  ref: '',
})
const loggingIn = ref(false)
const emailToken = ref('')

const mailLink = computed(() => {
  return `mailto:passwordchange@hi.pandafan.email?subject=${emailToken.value}:123123`
})

const buttonText = computed(() => {
  switch (state.value) {
    case 'login': return $t('login.sign_in_to')
    case 'signup': return $t('login.sign_up_to')
    case 'reset': return $t('login.reset.button')
    default: return ''
  }
})

async function onSubmit() {
  if (state.value !== 'reset' && (!loginInfo.value.email || !loginInfo.value.password)) {
    return useDialog().alert({
      title: 'Error',
      body: $t('login.email_or_password_please'),
      confirmButtonText: $t('ok'),
    })
  }
  loggingIn.value = true
  try {
    await submitFunc[state.value]()
    useTryTrackEvent(state.value, { status: 'success' })
    loggingIn.value = false
    afterWards()
  } catch (e: any) {
    useTryTrackEvent(state.value + '_error', {
      catched: true,
      status: 'error',
      error: e.message,
      action: state.value,
    })
    if (!e.data && e.message.startsWith('Failed to fetch (')) {
      return toast({
        title: $t(`errors.network_err.title`),
        description: $t(`errors.network_err.message`) + e.stack,
        variant: 'destructive',
      })
    }
    const realErr = e.data?.data || e.data
    const errDetail = realErr?.detail || e.data?.error
    const detailMsg = errDetail ? JSON.stringify(errDetail, null, 2) : ''
    toast({
      title: $t(`errors.${realErr?.message || e.message || e}.title`),
      description: $t(`errors.${realErr?.message || e.message || e}.message`) + detailMsg,
      variant: 'destructive',
    })
  }
  loggingIn.value = false
}

const submitFunc: any = {
  signup: async () => {
    const data = await user.signUp(loginInfo.value)
    if (data.jwt_token) {
      visitorSession.setToken(data.jwt_token)
    }
    return data
  },
  login: async () => {
    await visitorSession.login({
      email: loginInfo.value.email,
      password: loginInfo.value.password,
    })
  },
  reset: async () => {
    if (!loginInfo.value.email) {
      await useDialog().alert({
        title: 'Error',
        body: $t('login.email_please'),
        confirmButtonText: $t('ok'),
      })
      return (loggingIn.value = false)
    }
    try {
      const data = await user.requestPasswordReset({ email: loginInfo.value.email })
      useDialog().alert({
        title: $t('login.check_email'),
        body: $t('login.reset.reset_requested'),
        confirmButtonText: $t('ok'),
      })
      emailToken.value = data?.email_token as string
    } catch (err: Error | any) {
      if (err.data?.message === 'illegal email' || err.data?.message === 'not found') {
        return useDialog().alert({
          title: 'Error',
          body: $t('login.not_found'),
          confirmButtonText: $t('ok'),
        })
      }
      throw err
    }
  },
}

const afterWards = () => {
  if (!['signup', 'login'].includes(state.value)) return
  const $route = useRoute()
  if (['pricing', 'knowledges-knowledgeid'].includes(($route.name as string || '')?.toLowerCase())) {
    window?.location?.reload()
  } else {
    navigateTo(`/dashboard/${visitorSession.id}`)
  }
}
</script>

<template lang="pug">
.login-page-outer(:class="{ 'login-panel-embedded': embedded }")
  .login-page-wrap
    .auth-card.rounded-2xl.p-6(class="sm:p-8")
      //- Reset token display
      .reset-token-card.mb-5.rounded-xl.p-4.text-sm.text-muted-foreground(v-if="state === 'reset' && emailToken")
        h5.font-medium
          | {{ $t('login.reset.alt.a') }}
          br
          code.text-primary {{ emailToken }}:
          | {{ $t('login.reset.alt.b') }}
          br
          | {{ $t('login.reset.alt.c') }}
        br
        a.text-primary(:href="mailLink") passwordchange@hi.pandafan.email
        br
        | {{ $t('login.reset.alt.d') }}&nbsp;
        br
        p {{ emailToken }}:123123
        | &nbsp;{{ $t('login.reset.alt.e') }}&nbsp;
        code.text-primary 123123
        br
        | {{ $t('login.reset.alt.f') }}

      //- Form
      form(@submit.prevent.stop="onSubmit")
        //- Email
        div
          label.block.text-xs.font-bold.uppercase.tracking-wider.text-muted-foreground.mb-2 {{ $t('login.email') }}
          input.login-input.w-full.rounded-xl.px-4(
            type="email"
            v-model="loginInfo.email"
            placeholder="name@company.com"
            class="py-3"
          )

        //- Password
        div.mt-5(v-if="state !== 'reset'")
          .flex.items-center.justify-between.mb-2
            label.block.text-xs.font-bold.uppercase.tracking-wider.text-muted-foreground {{ $t('login.password') }}
            a.cursor-pointer.text-xs.font-bold.text-primary(
              v-if="state === 'login'"
              @click="state = 'reset'"
              class="hover:opacity-80"
            ) {{ $t('login.forgot.button') }}?
          input.login-input.w-full.rounded-xl.px-4(
            type="password"
            v-model="loginInfo.password"
            placeholder="••••••••"
            class="py-3"
          )

        //- Referral code
        div.mt-5(v-if="state === 'signup'")
          label.block.text-xs.font-bold.uppercase.tracking-wider.text-muted-foreground.mb-2 {{ $t('login.ref') }}
          input.login-input.w-full.rounded-xl.px-4(
            type="text"
            v-model="loginInfo.ref"
            :placeholder="$t('login.ref')"
            class="py-3"
          )

        //- Submit button
        .cta-button-wrap.mt-7
          button.cta-button(type="submit" :disabled="loggingIn")
            .cta-shadow
            .cta-front
              .login-spinner(v-if="loggingIn")
              span(v-else) {{ buttonText }}

      //- State switch links
      .mt-6.text-center
        p.text-sm.text-muted-foreground(v-if="state === 'login'")
          | {{ $t('login.no_account') }}
          |
          a.text-primary.font-bold.cursor-pointer(@click="state = 'signup'" class="hover:underline") {{ $t('login.signup_free') }}
        p.text-sm.text-muted-foreground(v-if="state === 'signup'")
          | {{ $t('login.has_account') }}
          |
          a.text-primary.font-bold.cursor-pointer(@click="state = 'login'" class="hover:underline") {{ $t('login.sign_in') }}
        p.text-sm.text-muted-foreground(v-if="state === 'reset'")
          | {{ $t('login.remember_password') }}
          |
          a.text-primary.font-bold.cursor-pointer(@click="state = 'login'" class="hover:underline") {{ $t('login.back_to_signin') }}
</template>

<style lang="postcss">

.login-page-outer {
  position: relative;
  width: 100%;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.login-page-wrap {
  position: relative;
  z-index: 1;
  padding: 20px;
  width: 100%;
  max-width: 460px;
  margin: 0 auto;
}

.login-panel-embedded .login-page-wrap {
  padding: 0;
  max-width: none;
}

/* ===== Auth card ===== */
.login-page-wrap .auth-card {
  background: color-mix(in oklch, var(--card) 80%, transparent);
  backdrop-filter: blur(12px);
  border: 1px solid var(--border);
  box-shadow: 0 20px 40px -12px oklch(0 0 0 / 0.15);
}

.dark .login-page-wrap .auth-card {
  background: oklch(0.15 0.005 260 / 0.9);
  border: 1px solid oklch(1 0 0 / 0.08);
  box-shadow:
    inset 0 1px 0 oklch(1 0 0 / 0.05),
    0 24px 48px -20px oklch(0 0 0 / 0.55),
    0 40px 90px -30px oklch(0 0 0 / 0.72);
}

/* ===== Reset token card ===== */
.login-page-wrap .reset-token-card {
  background: oklch(0.5 0 0 / 0.05);
  border: 1px solid var(--border);
}

/* ===== Input fields ===== */
.login-page-wrap .login-input {
  background: oklch(0.5 0 0 / 0.05);
  border: 1px solid var(--border);
  color: var(--foreground);
  transition: all 0.2s ease;
  font-size: 0.9rem;
}

.login-page-wrap .login-input::placeholder {
  color: var(--muted-foreground);
  opacity: 0.5;
}

.login-page-wrap .login-input:focus {
  border-color: oklch(0.648 0.2 131.684);
  box-shadow: 0 0 0 2px oklch(0.648 0.2 131.684 / 0.2);
  outline: none;
}

/* ===== CTA Button (Neubrutalism) ===== */
.login-page-wrap .cta-button-wrap {
  padding-bottom: 6px;
  padding-right: 6px;
}

.login-page-wrap .cta-button {
  position: relative;
  display: block;
  width: 100%;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  transition: all 0.2s ease;
}

.login-page-wrap .cta-button .cta-shadow {
  position: absolute;
  inset: 0;
  background-color: oklch(0.648 0.2 131.684);
  border-radius: 0.75rem;
  transform: translate(4px, 4px);
  transition: transform 0.15s ease;
  z-index: 0;
}

.login-page-wrap .cta-button:hover:not(:disabled) .cta-shadow {
  transform: translate(6px, 6px);
}

.login-page-wrap .cta-button:active:not(:disabled) .cta-shadow {
  transform: translate(2px, 2px);
}

.login-page-wrap .cta-button .cta-front {
  position: relative;
  background-color: var(--foreground);
  color: var(--background);
  border: 2px solid var(--foreground);
  border-radius: 0.75rem;
  padding: 0.8rem 1.5rem;
  font-weight: 700;
  font-size: 0.95rem;
  z-index: 1;
  transition: transform 0.15s ease;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 3rem;
}

.login-page-wrap .cta-button:hover:not(:disabled) .cta-front {
  transform: translate(-1px, -1px);
}

.login-page-wrap .cta-button:active:not(:disabled) .cta-front {
  transform: translate(2px, 2px);
}

.login-page-wrap .cta-button:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

/* ===== Loading spinner ===== */
.login-page-wrap .login-spinner {
  width: 18px;
  height: 18px;
  border: 2.5px solid oklch(0.5 0 0 / 0.2);
  border-top-color: var(--background);
  border-radius: 50%;
  animation: login-spin 0.6s linear infinite;
}

@keyframes login-spin {
  to { transform: rotate(360deg); }
}
</style>
